import { ChangeDetectionStrategy, Component, computed, inject, resource } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  EventqDataTable,
  EventqDataTableColumn,
  EventqLinedTab,
  EventqSortState,
} from '@ecam/eventq-components';
import { BrowserTabService } from '../../../../core/services/browser-tab.service';
import { autoReloadResource } from '../../../../core/utils/auto-reload-resource';
import { withPreviousResourceValue } from '../../../../core/utils/with-previous-resource-value';
import { activeQueueSort, queueTableColumn } from '../../queue-table-sort';
import { EventsDashboardService } from '../../services/events-dashboard.service';

@Component({
  selector: 'app-patrol-list-section',
  imports: [EventqLinedTab, EventqDataTable],
  templateUrl: './patrol-list-section.html',
  styleUrl: './patrol-list-section.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatrolListSection {
  private readonly eventsService = inject(EventsDashboardService);
  private readonly browserTabs = inject(BrowserTabService);

  readonly sort = computed(() => activeQueueSort(this.browserTabs.patrolSort()));

  readonly columns: EventqDataTableColumn[] = [
    queueTableColumn({ key: 'patrolStarted', label: 'Patrol Started', grow: 1.2 }),
    queueTableColumn({ key: 'location', label: 'Location/Event', grow: 1.6 }),
    queueTableColumn({ key: 'status', label: 'Status', grow: 0.9 }),
    queueTableColumn({ key: 'decisionOutcome', label: 'Outcome', grow: 1.1 }),
    queueTableColumn({ key: 'patrolNote', label: 'Patrol Note', grow: 1.2 }),
    queueTableColumn({ key: 'agent', label: 'Agent', grow: 1 }),
  ];

  private readonly patrolSource = resource({
    params: () => ({ sort: this.sort() }),
    loader: ({ params }) =>
      firstValueFrom(this.eventsService.getPatrolList(params.sort ?? undefined)),
  });

  readonly patrol = withPreviousResourceValue(this.patrolSource);
  readonly totalCount = computed(() => this.patrol.value()?.totalCount ?? 0);

  constructor() {
    autoReloadResource(this.patrolSource);
  }

  onSort(sort: EventqSortState): void {
    const next = activeQueueSort(sort);
    if (!next) {
      return;
    }
    this.browserTabs.updateActivePageState({ patrolSort: next });
  }
}
